// 在 ooxml 中规定，DrawingML 文本中的每个 Unicode 字符，字体样式可以是四种字体“插槽”中的任何一种：拉丁文（latin）、复合文本（cs）、东亚文本（ea）或符号（sym）
// For each Unicode character in DrawingML text, the font face can be any of four font “slots”:
// latin (§21.1.2.3.7), cs (§21.1.2.3.1), ea (§21.1.2.3.3), or sym (§21.1.2.3.10), as specified in the following table.
// For all ranges not explicitly called out below, the ea font shall be used. 具体见 ooxml 规范：https://ecma-international.org/publications-and-standards/standards/ecma-376/

// 在 ooxml 中 textrun 最终渲染的字体和 lang 属性无关，而且根据内容判断所属的字体类型 FontCategory，然后再从 fonts 属性中找对应类型的字体

// ooxml 中规定的拉丁 unicode 范围
const latinRanges = [
    { start: 0x0000, end: 0x007F },
    { start: 0x0080, end: 0x00A6 },
    { start: 0x00A9, end: 0x00AF },
    { start: 0x00B2, end: 0x00B3 },
    { start: 0x00B5, end: 0x00D6 },
    { start: 0x00D8, end: 0x00F6 },
    { start: 0x00F8, end: 0x058F },
    { start: 0x10A0, end: 0x10FF },
    { start: 0x1200, end: 0x137F },
    { start: 0x13A0, end: 0x177F },
    { start: 0x1D00, end: 0x1D7F },
    { start: 0x1E00, end: 0x1FFF },
    { start: 0x2000, end: 0x200B },
    { start: 0x2010, end: 0x2017 },
    { start: 0x2020, end: 0x2029 },
    { start: 0x2030, end: 0x2046 },
    { start: 0x204A, end: 0x245F },
    { start: 0x27C0, end: 0x2BFF },
    { start: 0xD835, end: 0xD835 },
    { start: 0xFB00, end: 0xFB17 },
    { start: 0xFE50, end: 0xFE6F },

];

// ooxml 中规定的符合文本(complex script) unicode 范围
const csRanges = [
    { start: 0x0590, end: 0x074F },
    { start: 0x0780, end: 0x07BF },
    { start: 0x0900, end: 0x109F },
    { start: 0x1780, end: 0x18AF },
    { start: 0x200C, end: 0x200F },
    { start: 0x202A, end: 0x202F },
    { start: 0x2670, end: 0x2671 },
    { start: 0xFB1D, end: 0xFB4F },
];

// ooxml 中规定的东亚文本(east asian) unicode 范围，同时 ooxml 规定对于未明确列出的范围，使用东亚字体
const eaRanges = [
    { start: 0x3099, end: 0x309A },
];

// ooxml 中规定的符号(symbol) unicode 范围
const symRanges = [
    { start: 0xF000, end: 0xF0FF },
];

// ooxml 中规定 0x2010-0x2029 使用 latin. 但是 2018 – 201F 这几个字符，在 ea 语言体系下，这些字符就使用 ea 字体渲染，如下是 ooxml 原文
// Except, for the quote characters in the range 2018 – 201F, use ea font if the text has one of the following
// language identifiers: ii-CN, ja-JP, ko-KR, zh-CN, zh-HK, zh-MO, zh-SG, zh-TW.
// 所以这个范围单独列举出来，根据语言属性决定
const latinOrEaRanges = [
    { start: 0x2018, end: 0x201F },
];

interface CharRange {
    start: number;
    end: number;
}

function isInRange(text: string, ranges: CharRange[]) {
    const charCode = text.charCodeAt(0);
    return ranges.some(range => charCode >= range.start && charCode <= range.end);
}

// ooxml 中规定的四种字体分类
export type FontCategory = 'latin' | 'cs' | 'ea' | 'sym';

/**
 * 根据文字内容获取字体类型
 * @param text
 * @param lang 这个字段指的是 uimodel 中 textrun 的 lang 属性，根据text-run-style-converter 的数据转换来看目前只有两个值：latin、ea
 */
export function getFontCategory(text: string, lang?: 'latin' | 'ea'): FontCategory {
    // 判断是否在范围内
    if (isInRange(text, symRanges)) {
        return 'sym';
    }
    if (isInRange(text, eaRanges)) {
        return 'ea';
    }
    if (isInRange(text, csRanges)) {
        return 'cs';
    }
    if (isInRange(text, latinRanges)) {
        return 'latin';
    }
    if (isInRange(text, latinOrEaRanges)) {
        // 除非指定了语言是 latin，否则默认是 ea
        return lang === 'latin' ? 'latin' : 'ea';
    }
    // 如果没有匹配到任何范围，返回 'ea'，因为 OOXML 规定对于未明确列出的范围，使用东亚字体
    return 'ea';
}
